import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCurrentContext } from '../lib/useCurrentContext.jsx';
import {
  LEVELS,
  DOMAINS,
  GOALS,
  GAMES_LIST,
  domainForGoal,
} from '../data/gamesCatalog.js';
import { recommendForResident, weakDomains } from '../lib/recommendations.js';
import {
  startSession,
  completeSession,
  engagementSummary,
  goalActivitySummary,
} from '../lib/sessions.js';
import './Dashboard.css';

// Cached lookup for the completed callout — built once at module load
const GAMES_LIST_BY_ID = new Map(GAMES_LIST.map((g) => [g.id, g]));

// The Brain Health dashboard.
//
// Identity-source-agnostic (Phase 2C refactor):
//   This page now reads from useCurrentContext rather than useResident
//   directly. It works for community residents (data under
//   /brain_health/accounts/), individual subscribers (under
//   /brain_health/users/), and eventually device-bundled users
//   (under /brain_health/devices/). The user record shape is the
//   same across all three roots (snake_case, mirrors /accounts/).
//
//   The historical variable names `account` and `accountId` are
//   preserved as local aliases below so the rest of this file stays
//   identical to the community-only version. Only the source of
//   those values, and the session-write target paths, changed.
//
// State machine:
//   1. No assessment yet → redirect to /onboarding/assessment
//   2. Assessment but no goals → redirect to /onboarding/goals
//   3. Both complete → show the real dashboard with recommendations,
//      domain breakdown, streak, and a just-completed callout if
//      they're returning from a game
//
// Returning from a game: the dashboard URL will have ?completed=<key>
// where <key> is the session push key we wrote on game start. We
// detect that on mount, complete the session, and surface a
// confirmation card that auto-clears after a short delay.
export default function Dashboard() {
  // Source identity from the unified context. `record` is the user's
  // data record (account, userRecord, or device — same shape).
  // `id` is the stable identifier. `rtdbPath` is the base path for
  // writes (sessions, last_active).
  const { record: account, id: accountId, rtdbPath } = useCurrentContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Track which game was just completed (for the celebration card).
  // Null when no recent completion.
  const [justCompleted, setJustCompleted] = useState(null);
  // Track the game card currently being launched (disables it
  // briefly so the user can't double-click and start two sessions).
  const [launchingGameId, setLaunchingGameId] = useState(null);

  // Onboarding gate
  useEffect(() => {
    if (!account) return;
    if (!account.assessment?.current) {
      navigate('/onboarding/assessment', { replace: true });
      return;
    }
    if (!account.goals || account.goals.length === 0) {
      navigate('/onboarding/goals', { replace: true });
      return;
    }
  }, [account, navigate]);

  // Handle ?completed=<sessionKey> on return from a game
  useEffect(() => {
    const sessionKey = searchParams.get('completed');
    if (!sessionKey || !rtdbPath || !account?.sessions) return;

    const session = account.sessions[sessionKey];
    if (session) {
      // Mark complete (idempotent — safe to re-do)
      completeSession({ basePath: rtdbPath, sessionKey }).catch((err) => {
        console.error('Failed to mark session complete:', err);
      });
      setJustCompleted({
        gameId: session.game_id,
        sessionKey,
      });
    }

    // Clean the URL so a refresh doesn't re-trigger the callout
    const next = new URLSearchParams(searchParams);
    next.delete('completed');
    setSearchParams(next, { replace: true });
  }, [searchParams, rtdbPath, account, setSearchParams]);

  // Auto-dismiss the celebration callout after a short delay
  useEffect(() => {
    if (!justCompleted) return;
    const timer = setTimeout(() => setJustCompleted(null), 8000);
    return () => clearTimeout(timer);
  }, [justCompleted]);

  // Compute recommendations + engagement metrics
  const current = account?.assessment?.current;
  const breakdown = current?.domain_breakdown;
  const recommendations = useMemo(() => {
    if (!account || !current) return [];
    return recommendForResident({
      level: current.level,
      goals: account.goals,
      breakdown,
      // recommendForResident uses this only as a stable rotation seed.
      // Any per-user identifier works — accountId for community
      // residents, uid for individuals, deviceId for devices.
      accountId,
      count: 5,
    });
  }, [account, accountId, current, breakdown]);

  const engagement = useMemo(
    () => engagementSummary(account?.sessions),
    [account?.sessions]
  );

  // 30-day per-goal activity (estimated minutes + play counts).
  // See goalActivitySummary for why these are estimated, not measured.
  const activity = useMemo(
    () => goalActivitySummary(account?.sessions, { windowDays: 30 }),
    [account?.sessions]
  );

  const weakSet = useMemo(() => weakDomains(breakdown), [breakdown]);

  if (!account || !current || !account.goals || account.goals.length === 0) {
    return null;
  }

  const firstName = (account.display_name || '').split(' ')[0];
  const levelInfo = LEVELS[current.level];
  const goalLabels = account.goals
    .map((g) => GOALS.find((x) => x.key === g))
    .filter(Boolean);

  // The breakdown panel works in DOMAIN space, but account.goals are
  // goal keys (A-E). Convert to the set of domain keys those goals
  // map to, so the "Your goal" tag lights up the right rows.
  const goalDomainSet = new Set(
    goalLabels.map((g) => g.domain).filter(Boolean)
  );

  // Build absolute return URL for game-side redirect
  const returnUrl = `${window.location.origin}/resident?completed=`;

  async function handleGameClick(rec, evt) {
    if (launchingGameId) return;  // prevent double-click
    // Blur immediately so the focus state doesn't linger while the
    // session write + redirect are in flight
    if (evt && evt.currentTarget && evt.currentTarget.blur) {
      evt.currentTarget.blur();
    }
    setLaunchingGameId(rec.game.id);
    try {
      const sessionKey = await startSession({
        basePath: rtdbPath,
        game: rec.game,
        mode: rec.mode,
        level: current.level,
      });
      // Use the catalog's URL (it knows the right path per game)
      // and add our return-URL contract. Some games have query
      // strings already (e.g. "/daily"); URL handles that cleanly.
      const gameUrl = new URL(rec.game.url);
      if (rec.mode) gameUrl.searchParams.set('mode', rec.mode);
      gameUrl.searchParams.set('return', `${returnUrl}${sessionKey}`);
      window.location.href = gameUrl.toString();
    } catch (err) {
      console.error('Failed to start session:', err);
      setLaunchingGameId(null);
    }
  }

  return (
    <div className="container dashboard">
      <span className="eyebrow fade-in-1">Your Dashboard</span>

      <h1 className="dashboard-greeting fade-in-2">
        Welcome back, <em>{firstName}</em>.
      </h1>

      {/* Just-completed callout */}
      {justCompleted ? (
        <CompletedCallout
          gameId={justCompleted.gameId}
          onDismiss={() => setJustCompleted(null)}
        />
      ) : null}

      {/* Streak indicator if active */}
      {engagement.streak > 0 ? (
        <div className="dashboard-streak fade-in-3">
          <span className="dashboard-streak-num">{engagement.streak}</span>
          <span className="dashboard-streak-text">
            day {engagement.streak === 1 ? 'started' : 'streak'} ·
            <span className="dashboard-streak-week">
              {' '}{engagement.weekCount} played this week
            </span>
          </span>
        </div>
      ) : engagement.totalPlays === 0 ? (
        <p className="dashboard-lede fade-in-3">
          Here's today's set, matched to your assessment and your goals.
          Pick one and we'll keep track of your progress.
        </p>
      ) : (
        <p className="dashboard-lede fade-in-3">
          Welcome back. Today's recommended games are below.
        </p>
      )}

      {/* Recommended games */}
      <section className="dashboard-section fade-in-4">
        <div className="dashboard-section-head">
          <h2 className="dashboard-section-title">Today's games</h2>
          <span className="dashboard-section-meta">
            Matched to your level and goals
          </span>
        </div>

        <ul className="dashboard-games">
          {recommendations.map((rec) => (
            <GameCard
              key={rec.game.id}
              rec={rec}
              launching={launchingGameId === rec.game.id}
              onClick={(evt) => handleGameClick(rec, evt)}
            />
          ))}
        </ul>
      </section>

      {/* Assessment + goals summary cards */}
      <section className="dashboard-section dashboard-summary">
        <div className="dashboard-grid">
          <article className="dashboard-card">
            <h3 className="dashboard-card-title">Your Assessment</h3>
            <div className="dashboard-card-level dashboard-card-level-lead">
              {levelInfo.label}
            </div>
            <p className="dashboard-card-meta">
              Completed{' '}
              {new Date(current.date).toLocaleDateString(undefined, {
                month: 'long', day: 'numeric', year: 'numeric',
              })}
            </p>
            <Link
              to="/onboarding/assessment"
              className="dashboard-card-edit"
            >
              Retake assessment
            </Link>
          </article>

          <article className="dashboard-card">
            <h3 className="dashboard-card-title">Your Goals</h3>
            <ul className="dashboard-goals">
              {goalLabels.map((g) => {
                const domain = DOMAINS[g.domain];
                return (
                  <li key={g.key} className="dashboard-goal">
                    <span
                      className="dashboard-goal-mark"
                      style={{ background: domain.color }}
                      aria-hidden="true"
                    >
                      {domain.icon}
                    </span>
                    <span className="dashboard-goal-label">{g.label}</span>
                  </li>
                );
              })}
            </ul>
            <Link to="/onboarding/goals" className="dashboard-card-edit">
              Change goals
            </Link>
          </article>
        </div>
      </section>

      {/* 30-day activity — effort by goal */}
      {activity.totalPlays > 0 ? (
        <section className="dashboard-section">
          <div className="dashboard-section-head">
            <h2 className="dashboard-section-title">Your activity</h2>
            <span className="dashboard-section-meta">
              Last 30 days
            </span>
          </div>
          <GoalActivity activity={activity} goals={goalLabels} />
        </section>
      ) : null}
    </div>
  );
}

// =================================================================
// GameCard — single recommended game
// =================================================================
function GameCard({ rec, launching, onClick }) {
  const { game, reasons } = rec;
  // Primary domain comes from the game's FIRST goal in the new model
  // (game.goals[0]). Guard against a game with no goals so a missing
  // domain renders the neutral mark instead of crashing.
  const primaryDomain = domainForGoal(game.goals?.[0]) || {
    color: '#690FC9',
    icon: '🧠',
  };
  // Track image load failure so we can fall back to the icon mark.
  // Most of the time this stays false and we never re-render with
  // the fallback. The fallback exists so a missing/slow image
  // doesn't show an empty thumbnail-shaped void.
  const [imageOk, setImageOk] = useState(true);

  // Build the reason badges. Prefer goal-match labels over weak-area
  // labels when both apply to the same domain (they shouldn't due
  // to the dedup logic in scoreGame, but defense in depth).
  const reasonBadges = reasons.map((r) => {
    const d = DOMAINS[r.domain];
    if (!d) return null;
    if (r.kind === 'goal' || r.kind === 'goal-secondary') {
      return {
        key: `${r.kind}-${r.domain}`,
        label: `Exercises ${d.label.toLowerCase()}`,
        tone: 'goal',
      };
    }
    if (r.kind === 'weak') {
      return {
        key: `weak-${r.domain}`,
        label: `Strengthens ${d.label.toLowerCase()}`,
        tone: 'weak',
      };
    }
    return null;
  }).filter(Boolean);

  return (
    <li className="dashboard-game">
      <button
        type="button"
        className="dashboard-game-card"
        onClick={onClick}
        disabled={launching}
      >
        {game.image && imageOk ? (
          <span className="dashboard-game-thumb" aria-hidden="true">
            <img
              src={game.image}
              alt=""
              className="dashboard-game-thumb-img"
              loading="lazy"
              onError={() => setImageOk(false)}
            />
          </span>
        ) : (
          <span
            className="dashboard-game-mark"
            style={{ background: primaryDomain.color }}
            aria-hidden="true"
          >
            {primaryDomain.icon}
          </span>
        )}
        <div className="dashboard-game-body">
          <span className="dashboard-game-name">{game.name}</span>
          {game.short_description ? (
            <span className="dashboard-game-tagline">{game.short_description}</span>
          ) : null}
          {reasonBadges.length > 0 ? (
            <div className="dashboard-game-badges">
              {reasonBadges.map((b) => (
                <span
                  key={b.key}
                  className={`dashboard-game-badge dashboard-game-badge-${b.tone}`}
                >
                  {b.label}
                </span>
              ))}
            </div>
          ) : null}
        </div>
        <span className="dashboard-game-arrow" aria-hidden="true">
          {launching ? '…' : '→'}
        </span>
      </button>
    </li>
  );
}

// =================================================================
// CompletedCallout — celebration when returning from a game
// =================================================================
function CompletedCallout({ gameId, onDismiss }) {
  // Resolve the friendly game name from the catalog if we can
  const gameName = useMemo(() => {
    const g = GAMES_LIST_BY_ID.get(gameId);
    return g ? g.name : 'that game';
  }, [gameId]);

  return (
    <div className="dashboard-completed">
      <span className="dashboard-completed-mark" aria-hidden="true">✓</span>
      <div className="dashboard-completed-body">
        <span className="dashboard-completed-title">
          Nice work playing <em>{gameName}</em>.
        </span>
        <span className="dashboard-completed-meta">
          Your progress is being tracked.
        </span>
      </div>
      <button
        type="button"
        className="dashboard-completed-dismiss"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// =================================================================
// GoalActivity — 30-day effort per selected goal (estimated)
//
// Shows one row per goal the resident has chosen: a proportional bar
// of estimated minutes, plus the play count. The bar scales relative
// to the busiest goal so the comparison is legible even when the
// absolute numbers are small. A total line sits below.
//
// Minutes are ESTIMATED (catalog estimated_minutes per play), not
// measured — see goalActivitySummary for the rationale. The copy
// says "estimated" so we don't overclaim precision.
// =================================================================
function GoalActivity({ activity, goals }) {
  // Largest per-goal minutes among the resident's chosen goals — used
  // to scale the bars. Guard against 0 so we never divide by zero.
  const maxMinutes = Math.max(
    1,
    ...goals.map((g) => activity.byGoal[g.key]?.minutes || 0)
  );

  return (
    <div className="dashboard-activity">
      <ul className="dashboard-activity-list">
        {goals.map((g) => {
          const domain = DOMAINS[g.domain];
          const stat = activity.byGoal[g.key] || { plays: 0, minutes: 0 };
          const pct = Math.round((stat.minutes / maxMinutes) * 100);
          return (
            <li key={g.key} className="dashboard-activity-row">
              <div className="dashboard-activity-head">
                <span className="dashboard-activity-name">
                  <span
                    className="dashboard-domain-dot"
                    style={{ background: domain.color }}
                    aria-hidden="true"
                  />
                  {domain.label}
                </span>
                <span className="dashboard-activity-stat">
                  {stat.plays === 0 ? (
                    <em>Not yet played</em>
                  ) : (
                    <>
                      {stat.minutes} min
                      <span className="dashboard-activity-plays">
                        {' '}· {stat.plays}{' '}
                        {stat.plays === 1 ? 'session' : 'sessions'}
                      </span>
                    </>
                  )}
                </span>
              </div>
              <div className="dashboard-domain-bar">
                <div
                  className="dashboard-domain-bar-fill"
                  style={{
                    width: stat.minutes > 0 ? `${pct}%` : '0',
                    background: domain.color,
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>
      <p className="dashboard-activity-total">
        <strong>{activity.totalMinutes} estimated minutes</strong> across{' '}
        {activity.totalPlays}{' '}
        {activity.totalPlays === 1 ? 'session' : 'sessions'} in the last 30
        days.
      </p>
    </div>
  );
}

// =================================================================
// DomainBreakdown — bars for each assessed domain
// =================================================================
function DomainBreakdown({ breakdown, weakSet, goalDomains }) {
  // Render in the catalog's current domain order so it's stable and
  // always matches the live DOMAINS definition (no hardcoded keys).
  const DOMAIN_ORDER = Object.keys(DOMAINS);

  return (
    <div className="dashboard-domains">
      {DOMAIN_ORDER.map((key) => {
        const d = DOMAINS[key];
        if (!d) return null;
        const data = breakdown[key];
        const isAssessed = !!data && data.possible > 0;
        const pct = isAssessed ? Math.round((data.earned / data.possible) * 100) : null;
        const isWeak = weakSet.has(key);
        const isGoal = goalDomains.has(key);

        return (
          <div key={key} className="dashboard-domain">
            <div className="dashboard-domain-head">
              <span className="dashboard-domain-name">
                <span
                  className="dashboard-domain-dot"
                  style={{ background: d.color }}
                  aria-hidden="true"
                />
                {d.label}
              </span>
              <span className="dashboard-domain-pct">
                {isAssessed ? `${pct}%` : <em>Not assessed</em>}
              </span>
            </div>
            <div className="dashboard-domain-bar">
              <div
                className="dashboard-domain-bar-fill"
                style={{
                  width: isAssessed ? `${pct}%` : '0',
                  background: d.color,
                }}
              />
            </div>
            <div className="dashboard-domain-labels">
              {isWeak ? (
                <span className="dashboard-domain-tag dashboard-domain-tag-weak">
                  Room to grow
                </span>
              ) : null}
              {isGoal ? (
                <span className="dashboard-domain-tag dashboard-domain-tag-goal">
                  Your goal
                </span>
              ) : null}
              {!isAssessed ? (
                <span className="dashboard-domain-tag dashboard-domain-tag-quiet">
                  Not covered by this version of the assessment
                </span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
